import {
  ActionIcon,
  Box,
  Button,
  Fieldset,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import type { UseFormReturnType } from '@mantine/form';
import type { TConfigTelegramChatRouting, TConfigTelegramRelayChat } from '../store/types';

const reactionOptions = [
  { value: '', label: 'Не использовать' },
  ...['✅', '👍', '💯', '👌', '🙏', '🔥', '🎉', '👏'].map((value) => ({ value, label: value })),
];

type Props = {
  form: UseFormReturnType<TConfigTelegramChatRouting>;
  onAdd: () => void;
  onRemove: (index: number) => void;
};

export const TelegramRelayChats = ({ form, onAdd, onRemove }: Props) => (
  <Stack gap="md">
    <Box>
      <Title order={5}>Чаты без заявок</Title>
      <Text size="sm" c="dimmed" mt={4}>
        Бот передаст фото или файл чека в чат проверки без ID заявки.
        После подтверждения поставит 👌 и ответит на исходный чек. Реакция 👎 сообщает клиенту, что платёж не найден.
        Подтвердить может любой участник чата проверки.
      </Text>
      <Text size="sm" c="dimmed" mt={4}>
        Добавьте бота администратором в оба чата, чтобы он видел скриншоты и реакции.
      </Text>
    </Box>

    {form.getValues().relayChats.map((chat, index) => {
      const field = (name: keyof TConfigTelegramRelayChat) => `relayChats.${index}.${name}`;
      const options = reactionOptions.some((option) => option.value === chat.confirmationReaction)
        ? reactionOptions
        : [...reactionOptions, { value: chat.confirmationReaction, label: chat.confirmationReaction }];

      return (
        <Fieldset
          key={form.key(`relayChats.${index}`)}
          legend={`Чат без заявок ${index + 1}`}
        >
          <Stack gap="md">
            <Group justify="flex-end">
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label="Удалить чат без заявок"
                onClick={() => onRemove(index)}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Group>

            <TextInput
              label="Название"
              placeholder="Например, Клиент — переводы"
              withAsterisk
              key={form.key(field('name'))}
              {...form.getInputProps(field('name'))}
            />

            <TextInput
              label="ID исходного чата"
              description="Сюда клиент отправляет чек"
              placeholder="Например, -1001234567890"
              withAsterisk
              key={form.key(field('chatId'))}
              {...form.getInputProps(field('chatId'))}
            />

            <TextInput
              label="ID чата проверки"
              description="Сюда бот пересылает чек без подписи для подтверждения"
              placeholder="Например, -1009876543210"
              withAsterisk
              key={form.key(field('destinationChatId'))}
              {...form.getInputProps(field('destinationChatId'))}
            />

            <Select
              label="Реакция подтверждения"
              description="Поставьте эту реакцию на пересланный чек"
              data={options}
              allowDeselect={false}
              key={form.key(field('confirmationReaction'))}
              {...form.getInputProps(field('confirmationReaction'))}
            />

            <Select
              label="Дополнительная реакция подтверждения"
              description="Любая из выбранных реакций подтверждает чек"
              data={reactionOptions}
              allowDeselect={false}
              key={form.key(field('confirmationAlternateReaction'))}
              {...form.getInputProps(field('confirmationAlternateReaction'))}
            />

            <Select
              label="Ещё одна реакция подтверждения"
              data={reactionOptions}
              allowDeselect={false}
              key={form.key(field('confirmationThirdReaction'))}
              {...form.getInputProps(field('confirmationThirdReaction'))}
            />

            <TextInput
              label="ID пользовательской реакции (необязательно)"
              description="Если используете пользовательскую реакцию, укажите её Telegram ID. Обычным реакциям ID не нужен."
              placeholder="ID эмодзи"
              maxLength={128}
              key={form.key(field('confirmationCustomEmojiId'))}
              {...form.getInputProps(field('confirmationCustomEmojiId'))}
            />

            <TextInput
              label="Кого отмечать в чате проверки"
              description="Пока чек не подтверждён, бот будет отмечать этих участников каждые 5 минут. Если поле пустое, напоминаний не будет."
              placeholder="@username1 @username2"
              key={form.key(field('reminderUsernames'))}
              {...form.getInputProps(field('reminderUsernames'))}
            />

            <TextInput
              label="Слово или фраза подтверждения"
              description="Ответьте этим текстом на пересланный чек. Регистр не важен. Можно оставить пустым, если выбрана реакция"
              placeholder="есть"
              maxLength={200}
              key={form.key(field('confirmationText'))}
              {...form.getInputProps(field('confirmationText'))}
            />

            <Textarea
              label="Ответ клиенту"
              description="Бот отправит его в исходный чат в ответ на чек после подтверждения"
              autosize
              minRows={2}
              maxLength={4096}
              withAsterisk
              key={form.key(field('successMessage'))}
              {...form.getInputProps(field('successMessage'))}
            />
          </Stack>
        </Fieldset>
      );
    })}

    <Group justify="flex-start">
      <Button
        type="button"
        variant="outline"
        leftSection={<IconPlus size={16} />}
        onClick={onAdd}
      >
        Добавить чат без заявок
      </Button>
    </Group>
  </Stack>
);
